import { Event } from '@statsbomb/parachute-types';
import { VideoSport } from '@statsbomb/video-client-js';
import { PERIODS } from '@/consts/periods';
import { EventDescription, VisEvent } from '@/types/event';
import { NestedObject } from '@/types/object';
import { SelectedVideoIds } from '@/types/video';

// Return the video IDs in order of priority. The order of priority is:
// 1. videoEventId - If this is present it will always be used. This means we've already generated an ID.
// 2. originatingArqamEventId - This means the event is derived so we pass the originating Arqam event ID.
// 3. arqamEventId - The standard ID to return for these events.
// 4. eventId - The event ID is used as a fallback if no other IDs are available. This is the event ID from this event
//              itself which means that if this event is derived we won't pass the derived event ID.
//              Once Tornado is collecting all games this will be the standard ID as the Arqam IDs will not be generated
//              for newly collected games.
// 5. Empty string - realistically not reachable as the eventId should always be present, but this is a fallback
const getVideoIds = ({
  eventId,
  arqamEventId,
  originatingArqamEventId,
  videoEventId,
}: {
  eventId: unknown;
  originatingArqamEventId?: unknown;
  arqamEventId?: unknown;
  videoEventId?: unknown;
}) => ({
  eventId: String(eventId),
  videoEventId: String(videoEventId || originatingArqamEventId || arqamEventId || eventId || ''),
});

export const getVideoIdsFromEvent = (event: Event) =>
  getVideoIds({
    eventId: event.event_id,
    originatingArqamEventId: event.attributes.originating_arqam_event_id,
    arqamEventId: event.attributes.arqam_event_id,
  });

// Get the video Ids based on a passed in 'Flat' event object where there is no nesting so the keys are in the format
// `event.attributes.arqamEventId` rather than { event: { attributes: { arqamEventId: ... } } }
export const getVideoIdsFromFlatEvent = (event: NestedObject): SelectedVideoIds => {
  const eventId = event['event.eventId'];
  const originatingArqamEventId = event['event.attributes.originatingArqamEventId'];
  const arqamEventId = event['event.attributes.arqamEventId'];

  return getVideoIds({
    eventId,
    originatingArqamEventId,
    arqamEventId,
  });
};

export const getVideoIdsFromVisEventOrDescriptions = (events: (VisEvent | EventDescription)[]) =>
  events.map(getVideoIds);

export const formatGameVideoReference = (gameId: string, period: number) => ({
  matchId: gameId,
  period,
  sport: VideoSport.Soccer,
});

export const formatGameVideoReferences = (gameId: string, periods: readonly number[] = PERIODS) =>
  Array.from({ length: periods.length }).map((_, i) => formatGameVideoReference(gameId, i + 1));
